import { LoadTender } from '../../../Api/Trinium/Endpoints/EDI/types';
import { GridColDef } from '@mui/x-data-grid-pro';
import StackedTextCell from '../ColumnDefinitions/StackedTextCell';
import MonetaryAmountCell from '../ColumnDefinitions/MonetaryAmountCell';
import { formatCityState } from '@/Infrastructure/Utilities/string.formatter';
import EdiTypeCell from './ColumnDefinitions/EdiTypeCell';
import RenderHeader from '../RenderHeader';
import { DateAfterOrEqualToValueOperator, DateBeforeOrEqualToValueOperator } from '../Filters';
import { caseInsensitiveIncludes } from '../Dispatch/columns';
import OrderNumberCell from './ColumnDefinitions/OrderNumberCell';
import EdiStatusCell from './ColumnDefinitions/EdiStatusCell';
import RowActionsMenuCell from '../ColumnDefinitions/RowActionsMenuCell';
import RowActionList from './ColumnDefinitions/RowActionList';
import RenderCell from '../RenderCell';

import styles from '../RenderCell.module.css';

export const ediLoadTenderColumns: GridColDef<LoadTender>[] = [
	{
		field: 'rowActions',
		headerName: '',
		renderCell: (params) => (
			<RowActionsMenuCell
				row={params.row}
				rowIdProperty='id'
			>
				<RowActionList />
			</RowActionsMenuCell>
		),
		cellClassName: styles['row-action-cell'],
		display: 'flex',
		minWidth: 24,
		width: 24,
		sortable: false,
		hideSortIcons: true,
		disableColumnMenu: true,
		disableReorder: true,
		filterable: false
	},
	{
		field: 'dateCreated',
		renderHeader: () => (
			<RenderHeader
				primaryText='Imported'
				secondaryText='Time'
			/>
		),
		renderCell: (params) => (
			<StackedTextCell
				primaryText={params.row.dateCreated?.shortDateExtended}
				primaryClassName={styles.bold}
				secondaryText={params.row.dateCreated?.time}
				secondaryClassName={styles['secondary-text']}
			/>
		),
		width: 80,
		cellClassName: styles.cell,
		sortable: false,
		hideSortIcons: true,
		disableColumnMenu: true,
		disableReorder: true,
		filterable: false,
		filterOperators: [DateAfterOrEqualToValueOperator, DateBeforeOrEqualToValueOperator],
		getApplyQuickFilterFn: (value: string) => {
			return (_, row): boolean => {
				const { dateCreated } = row;
				return caseInsensitiveIncludes(value, dateCreated?.dateTime);
			};
		}
	},
	{
		field: 'status',
		renderHeader: () => <RenderHeader primaryText='Status' />,
		renderCell: (params) => <EdiStatusCell row={params.row} />,
		cellClassName: styles.cell,
		width: 78,
		sortable: false,
		disableColumnMenu: true,
		disableReorder: true,
		filterable: false,
		getApplyQuickFilterFn: (value: string) => {
			return (_, row): boolean => {
				const { status } = row;
				return caseInsensitiveIncludes(value, status);
			};
		}
	},
	{
		field: 'type',
		renderHeader: () => (
			<RenderHeader
				primaryText='Type'
				secondaryText='Statuses'
			/>
		),
		width: 120,
		renderCell: (params) => <EdiTypeCell row={params.row} />,
		// MUI DataGrid keys cell re-renders on the column's value. Without this, toggling isHaz/isOverweight/isLive
		// in the cache (e.g., after a PATCH) doesn't repaint the tags because `row.type` is always undefined.
		valueGetter: (_, row) =>
			`${row.status}|||${String(row.isExport)}|||${String(row.isLive)}|||${String(row.isHaz)}|||${String(row.isOverweight)}`,
		cellClassName: styles.cell,
		sortable: false,
		disableColumnMenu: true,
		disableReorder: true,
		filterable: false,
		getApplyQuickFilterFn: (value: string) => {
			return (_, row): boolean => {
				const { dateCreated } = row;
				return caseInsensitiveIncludes(value, dateCreated?.dateTime);
			};
		}
	},
	{
		field: 'custId',
		renderHeader: () => (
			<RenderHeader
				primaryText='Customer'
				secondaryText='City, State, Zip'
			/>
		),
		renderCell: (params) => (
			<StackedTextCell
				primaryText={params.row.cust?.name ?? '-'}
				primaryClassName={styles.bold}
				secondaryText={formatCityState(params.row.cust?.city, params.row.cust?.state, params.row.cust?.zip)}
			/>
		),
		cellClassName: styles.cell,
		flex: 1,
		minWidth: 90,
		sortable: false,
		disableColumnMenu: true,
		disableReorder: true,
		filterable: false,
		getApplyQuickFilterFn: (value: string) => {
			return (_, row): boolean => {
				const { custId, cust } = row;
				return caseInsensitiveIncludes(value, [custId, cust?.name, cust?.addr1, cust?.addr2, cust?.city, cust?.state]);
			};
		}
	},
	{
		field: 'custRef',
		renderHeader: () => <RenderHeader primaryText='Cust. Ref. #' />,
		renderCell: (params) => (
			<StackedTextCell
				primaryText={params.row.custRefNo}
				primaryClassName={styles.bold}
			/>
		),
		cellClassName: styles.cell,
		flex: 1,
		minWidth: 90,
		sortable: false,
		disableColumnMenu: true,
		disableReorder: true,
		filterable: false,
		getApplyQuickFilterFn: (value: string) => {
			return (_, row): boolean => {
				const { custRefNo } = row;
				return caseInsensitiveIncludes(value, [custRefNo]);
			};
		}
	},
	{
		field: 'cntrId',
		renderHeader: () => <RenderHeader primaryText='Container' />,
		renderCell: (params) => (
			<StackedTextCell
				primaryText={params.row.cntrId ?? '-'}
				primaryClassName={styles.bold}
			/>
		),
		cellClassName: styles.cell,
		flex: 1,
		minWidth: 90,
		sortable: false,
		disableColumnMenu: true,
		disableReorder: true,
		filterable: false,
		getApplyQuickFilterFn: (value: string) => {
			return (_, row): boolean => {
				const { cntrId } = row;
				return caseInsensitiveIncludes(value, [cntrId]);
			};
		}
	},
	{
		field: 'pickup',
		renderHeader: () => (
			<RenderHeader
				primaryText='Pickup Loc.'
				secondaryText='Address'
			/>
		),
		renderCell: (params) => (
			<StackedTextCell
				primaryText={params.row.pu?.name ?? '-'}
				primaryClassName={styles.bold}
				secondaryText={formatCityState(params.row.pu?.city, params.row.pu?.state, params.row.pu?.zip)}
			/>
		),
		cellClassName: styles.cell,
		flex: 1,
		minWidth: 90,
		sortable: false,
		disableColumnMenu: true,
		disableReorder: true,
		filterable: false,
		getApplyQuickFilterFn: (value: string) => {
			return (_, row): boolean => {
				const { puId, pu, puRefNo } = row;
				return caseInsensitiveIncludes(value, [puId, pu?.name, pu?.addr1, pu?.city, pu?.state, puRefNo]);
			};
		}
	},
	{
		field: 'delv',
		renderHeader: () => (
			<RenderHeader
				primaryText='Delivery Loc.'
				secondaryText='Address'
			/>
		),
		renderCell: (params) => (
			<StackedTextCell
				primaryText={params.row.deliveries[0]?.name ?? '-'}
				primaryClassName={styles.bold}
				secondaryText={formatCityState(params.row.deliveries[0]?.city, params.row.deliveries[0]?.state, params.row.deliveries[0]?.zip)}
			/>
		),
		cellClassName: styles.cell,
		flex: 1,
		minWidth: 90,
		sortable: false,
		disableColumnMenu: true,
		disableReorder: true,
		filterable: false,
		getApplyQuickFilterFn: (value: string) => {
			return (_, row): boolean => {
				const { deliveries, delvRefNo } = row;
				const delv = deliveries.length > 0 ? deliveries[0] : undefined;
				return caseInsensitiveIncludes(value, [delv?.id, delv?.name, delv?.addr1, delv?.city, delv?.state, delvRefNo]);
			};
		}
	},
	{
		field: 'delvDate',
		renderHeader: () => (
			<RenderHeader
				primaryText='Scheduled Delivery'
				secondaryText=''
			/>
		),
		renderCell: (params) => (
			<StackedTextCell
				primaryText={params.row.deliveries[0]?.scheduledFromDate?.dateTime ?? '-'}
				primaryClassName={styles.bold}
				secondaryText={params.row.deliveries[0]?.scheduledToDate?.dateTime ?? '-'}
			/>
		),
		cellClassName: styles.cell,
		flex: 1,
		minWidth: 90,
		sortable: false,
		disableColumnMenu: true,
		disableReorder: true,
		filterable: false,
		getApplyQuickFilterFn: (value: string) => {
			return (_, row): boolean => {
				const { deliveries } = row;
				const delivery = deliveries.length > 0 ? deliveries[0] : undefined;
				return caseInsensitiveIncludes(value, [delivery?.scheduledFromDate?.dateTime, delivery?.scheduledToDate?.dateTime]);
			};
		}
	},
	{
		field: 'rate',
		renderHeader: () => <RenderHeader primaryText='Rate' />,
		renderCell: (params) => <MonetaryAmountCell value={params.row.rate} />,
		sortable: false,
		disableColumnMenu: true,
		disableReorder: true,
		filterable: false
	},
	{
		field: 'fuel',
		headerName: 'Fuel',
		renderHeader: () => <RenderHeader primaryText='Fuel' />,
		renderCell: (params) => <MonetaryAmountCell value={params.row.fuel} />,
		sortable: false,
		disableColumnMenu: true,
		disableReorder: true,
		filterable: false
	},
	{
		field: 'jobCatId',
		renderHeader: () => <RenderHeader primaryText='Order' />,
		renderCell: (params) => <OrderNumberCell row={params.row} />,
		sortable: false,
		disableColumnMenu: true,
		disableReorder: true,
		filterable: false,
		getApplyQuickFilterFn: (value: string) => {
			return (_, row): boolean => {
				const { jobCategory, ordNo } = row;
				return caseInsensitiveIncludes(value, [jobCategory?.id, jobCategory?.name, ordNo]);
			};
		}
	},
	{
		field: 'dateModified',
		renderHeader: () => (
			<RenderHeader
				primaryText='Modified'
				secondaryText='Time'
			/>
		),
		renderCell: (params) => (
			<RenderCell
				primarySlot={
					<span
						className={styles.bold}
						style={{ fontSize: '14px' }}
					>
						{params.row.dateModified?.shortDateExtended}
					</span>
				}
				secondarySlot={<span className={styles['secondary-text']}>{params.row.dateModified?.time}</span>}
				tertiarySlot={<span className={styles['secondary-text']}>{params.row.modUsrCode}</span>}
			/>
		),

		sortable: false,
		disableColumnMenu: true,
		disableReorder: true,
		filterable: false,
		getApplyQuickFilterFn: (value: string) => {
			return (_, row): boolean => {
				const { dateModified } = row;
				return caseInsensitiveIncludes(value, dateModified?.dateTime);
			};
		}
	}
];
